import React, { useState, useEffect, useRef } from 'react';
import Highcharts from 'highcharts/highstock';
import {
  isolateChartSeriesOnLegendFocus,
} from '../../../../utils/highchartsConfig';
import FlexFuelDetailToggleGroup from './FlexFuelDetailToggleGroup';

/** Demand = short solid line; stacked series = filled circle (matches Highcharts line legend). */
function HourlyLegendSymbol({ item }) {
  const isDemand = item.id === 'demand' || item.name === 'Demand';
  if (isDemand) {
    return (
      <span
        className="inline-block shrink-0 rounded-[1px]"
        style={{ width: 16, height: 2.5, backgroundColor: item.color }}
        aria-hidden
      />
    );
  }
  return (
    <span
      className="inline-block h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-black/5"
      style={{ backgroundColor: item.color }}
      aria-hidden
    />
  );
}

/** HTML legend above the desktop hourly chart (plot height stays fixed at 420px). */
function HourlyChartDesktopLegend({
  series,
  chartRef,
  onHeightChange,
  className,
  flexFuelToggles = null,
  onToggleFlexFuel = null,
  flexFuelToggleBusy = false,
  flexFuelToggleActiveFuel = null,
}) {
  const [, setLegendTick] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return undefined;
    const bump = () => setLegendTick((t) => t + 1);
    Highcharts.addEvent(chart, 'redraw', bump);
    return () => {
      Highcharts.removeEvent(chart, 'redraw', bump);
    };
  }, [chartRef, series]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof onHeightChange !== 'function') return undefined;

    const reportHeight = () => {
      const next = Math.ceil(el.getBoundingClientRect().height);
      if (next > 0) onHeightChange(next);
    };

    reportHeight();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', reportHeight);
      return () => window.removeEventListener('resize', reportHeight);
    }

    const observer = new ResizeObserver(reportHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [onHeightChange, series, flexFuelToggles]);

  if (!series?.length) return null;

  const findChartSeries = (id, name) => {
    const chart = chartRef.current?.chart;
    if (!chart) return null;
    return chart.series.find((s) => s.options?.id === id || s.name === name) || null;
  };

  const toggleSeries = (id, name) => {
    const target = findChartSeries(id, name);
    if (!target) return;
    target.setVisible(!target.visible, false);
    target.chart.redraw(false);
    setLegendTick((t) => t + 1);
  };

  const isolateSeries = (e, id, name) => {
    e.preventDefault();
    const target = findChartSeries(id, name);
    if (!target) return;
    isolateChartSeriesOnLegendFocus(target.chart, target);
    setLegendTick((t) => t + 1);
  };

  return (
    <div
      ref={containerRef}
      className={`hourly-chart-legend shrink-0 px-3 sm:px-4 lg:px-5 pt-2 pb-0${className ? ` ${className}` : ''}`}
      aria-label="Chart legend"
    >
      <FlexFuelDetailToggleGroup
        flexFuelToggles={flexFuelToggles}
        onToggleFlexFuel={onToggleFlexFuel}
        flexFuelToggleBusy={flexFuelToggleBusy}
        flexFuelToggleActiveFuel={flexFuelToggleActiveFuel}
      />
      <div
        className="flex flex-wrap items-center justify-start content-start gap-x-2 gap-y-1.5"
        role="group"
      >
        {series.map((item) => {
          const hcSeries = findChartSeries(item.id, item.name);
          const visible = hcSeries ? hcSeries.visible !== false : item.visible !== false;
          return (
            <button
              key={item.id || item.name}
              type="button"
              data-hourly-legend-item={item.id || item.name}
              onClick={() => toggleSeries(item.id, item.name)}
              onContextMenu={(e) => isolateSeries(e, item.id, item.name)}
              className={`hourly-chart-legend-item inline-flex w-max max-w-full shrink-0 grow-0 basis-auto items-center gap-1.5 rounded-md px-1 py-0.5 text-[11px] font-medium leading-4 whitespace-nowrap transition-colors ${
                visible
                  ? 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  : 'text-slate-400 line-through opacity-55 hover:bg-slate-50/70'
              }`}
              title={`${visible ? 'Hide' : 'Show'} ${item.name} (right-click to isolate)`}
            >
              <HourlyLegendSymbol item={item} />
              <span>{item.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default HourlyChartDesktopLegend;
